import { XMarkIcon } from '@heroicons/react/20/solid';
import { MapPin, Notebook, User, User2, Warehouse } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import AddressCapture from './AddressCapture';
import BusinessDetailsCapture from './BusinessDetails';
import Confirmation from './Confirmation';
import ParticipantInfo from './ParticipantInfo';
import UserDetailsCapture from './UserDetails';

type CheckoutPopupProps = {
  enabledStages: any;
  onClose: () => void;
  show: boolean;
  isUserDetailsRequired: boolean;
  onSubmit: (data: any, options?: any) => void;
  isSubmitInProgress: boolean;
  checkoutType?: 'CART' | 'QUICK';
  paymentModes?: { vipps: boolean; stripe: boolean; dpo: boolean };
  totalAmount?: number;
  participantCountData?: any[];
};

const CheckoutPopup: FC<CheckoutPopupProps> = ({
  show,
  onClose,
  enabledStages,
  isUserDetailsRequired,
  isSubmitInProgress,
  onSubmit,
  checkoutType = 'QUICK',
  paymentModes = { vipps: false, stripe: false, dpo: false },
  totalAmount = 0,
  participantCountData = [],
}) => {
  const [stage, setStage] = useState(0);
  const [participantData, setParticipantData] = useState(null);
  const [consentForPhotos, setConsentForPhotos] = useState(false);
  const [addressData, setAddressData] = useState(null);
  const [businessDetails, setBusinessDetails] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const stages = useMemo(() => {
    const stagesList = [] as { name: string; icon: any; id: string }[];

    if (isUserDetailsRequired) {
      stagesList.push({ name: 'User Details', icon: User2, id: 'user' });
    }

    if (enabledStages?.isChildInfoRequired) {
      stagesList.push({ name: 'Participant Info', icon: User, id: 'participant' });
    }

    if (enabledStages?.isAddressRequired) {
      stagesList.push({ name: 'Address', icon: MapPin, id: 'address' });
    }

    if (enabledStages?.isBusinessDetailsRequired) {
      stagesList.push({ name: 'Business', icon: Warehouse, id: 'business' });
    }

    stagesList.push({ name: 'Confirm', icon: Notebook, id: 'confirm' });

    return stagesList;
  }, [enabledStages, isUserDetailsRequired]);

  const handleNext = () => {
    setStage(stage + 1);
  };

  const handleBack = () => {
    setStage(stage - 1);
  };

  const handleStageChange = (newStage: any) => {
    if (newStage <= stage) {
      setStage(newStage);
    }
  };

  const handleGoToTab = (index: number) => {
    setStage(index);
  };

  const renderStageComponent = () => {
    const current = stages[stage];
    if (!current) return null;

    switch (current.id) {
      case 'user':
        return <UserDetailsCapture onNext={handleNext} setUserData={setUserDetails} userData={userDetails} />;
      case 'participant':
        return (
          <ParticipantInfo
            stage={stage}
            onBack={handleBack}
            onNext={handleNext}
            setParticipantData={setParticipantData}
            participantData={participantData}
            participantCountData={participantCountData}
            consentForPhotos={consentForPhotos}
            setConsentForPhotos={setConsentForPhotos}
          />
        );
      case 'address':
        return (
          <AddressCapture
            onNext={handleNext}
            setAddressData={setAddressData}
            addressData={addressData}
            stage={stage}
            onBack={handleBack}
          />
        );
      case 'business':
        return (
          <BusinessDetailsCapture
            stage={stage}
            onBack={handleBack}
            onNext={handleNext}
            setBusinessData={setBusinessDetails}
            businessData={businessDetails}
          />
        );
      case 'confirm':
      default:
        return (
          <Confirmation
            stage={stage}
            checkoutType={checkoutType}
            paymentModes={paymentModes}
            onBack={handleBack}
            data={{ participantData, addressData, businessDetails, userDetails, consentForPhotos }}
            stages={stages}
            isSubmitInProgress={isSubmitInProgress}
            onSubmit={onSubmit}
            totalAmount={totalAmount}
            handleGoToTab={handleGoToTab}
          />
        );
    }
  };

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-[9999999] bg-black/50">
      <div
        className="w-full md:w-[500px] rounded-md bg-white h-full md:h-[600px] flex flex-col"
        id="checkoutPopup"
      >
        <div className="w-full flex justify-between p-4 grow-0">
          <span className="text-[18px] text-slate-700 text-left">Checkout</span>
          <XMarkIcon
            className="w-7 h-7 rounded-full hover:bg-slate-200"
            onClick={(event) => {
              event.stopPropagation();
              onClose();
            }}
          />
        </div>
        <div className="p-4 grow flex flex-col h-[calc(100%-60px)]">
          <div className="flex justify-between mb-6 relative h-[75px] mt-4 grow-0">
            {stages.map((s, index) => (
              <button
                key={index}
                className={`flex flex-col items-center cursor-pointer z-10 ${
                  stage === index ? 'text-primary' : 'text-muted-foreground'
                }`}
                onClick={() => handleStageChange(index)}
              >
                <div className={`rounded-full p-2 ${stage === index ? 'bg-black text-white' : 'bg-muted'}`}>
                  <s.icon className="h-5 w-5" />
                </div>
                <span className="text-xs mt-1">{s.name}</span>
              </button>
            ))}
            <div className="absolute top-1/2 left-0 w-full h-0.5 bg-muted -z-10" />
          </div>
          <div className="border rounded-md h-[calc(100%-115px)]">{renderStageComponent()}</div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CheckoutPopup;
